<script setup>
import { computed, onMounted, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useStoreGetters } from 'dashboard/composables/store';
import EngagehubDashboardAPI from 'dashboard/api/engagehubDashboard';
import NextButton from 'dashboard/components-next/button/Button.vue';

const { t } = useI18n();
const route = useRoute();
const router = useRouter();
const getters = useStoreGetters();

const loading = ref(false);
const errored = ref(false);
const errorMessage = ref('');
const empty = ref(false);
const metrics = ref(null);
const generatedAt = ref(null);

// today in local timezone as YYYY-MM-DD
const todayIso = () => {
  const now = new Date();
  const off = now.getTimezoneOffset() * 60000;
  return new Date(now - off).toISOString().slice(0, 10);
};
const ISO_RE = /^\d{4}-\d{2}-\d{2}$/;
// initial date from URL (?data=) if valid, else today
const initialDate =
  ISO_RE.test(route.query.data || '') ? route.query.data : todayIso();
const selectedDate = ref(initialDate);
const effectiveDate = ref(initialDate);

const accountId = computed(() => route.params.accountId);
const currentUser = computed(() => getters.getCurrentUser.value || {});
const currentAccount = computed(
  () => getters['accounts/getAccount'].value(Number(accountId.value)) || {}
);

// ---- formatting helpers (nil-safe) -----------------------------------------
const DASH = '—';
const brl = value =>
  value === null || value === undefined
    ? DASH
    : Number(value).toLocaleString('pt-BR', {
        style: 'currency',
        currency: 'BRL',
      });
const pct = value =>
  value === null || value === undefined
    ? DASH
    : `${Number(value).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}%`;
const dec = value =>
  value === null || value === undefined
    ? DASH
    : Number(value).toLocaleString('pt-BR', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
const int = value =>
  value === null || value === undefined ? DASH : Number(value).toLocaleString('pt-BR');

const m = computed(() => metrics.value || {});

// ---- color rules -----------------------------------------------------------
// returns 'pos' | 'neg' | 'neutral'
const tone = value => {
  if (value === null || value === undefined || value === 0) return 'neutral';
  return value > 0 ? 'pos' : 'neg';
};
const roiTone = value => {
  if (value === null || value === undefined) return 'neutral';
  return value >= 1 ? 'pos' : 'neg';
};
const toneClass = tn =>
  ({
    pos: 'text-n-teal-11',
    neg: 'text-n-ruby-11',
    neutral: 'text-n-slate-11',
  })[tn];

// ---- data fetching ---------------------------------------------------------
const syncUrl = () => {
  // keep ?data= in the URL so F5 / sharing preserves the date, without
  // pushing history entries or leaking across accounts (account is in path)
  router.replace({
    query: { ...route.query, data: selectedDate.value },
  });
};

const fetchData = async () => {
  loading.value = true;
  errored.value = false;
  errorMessage.value = '';
  empty.value = false;
  try {
    const { data } = await EngagehubDashboardAPI.fetch(selectedDate.value);
    effectiveDate.value = data.date || selectedDate.value;
    if (data.empty) {
      empty.value = true;
      metrics.value = null;
    } else {
      metrics.value = data.metrics;
      generatedAt.value = data.generated_at;
    }
  } catch (error) {
    errored.value = true;
    errorMessage.value = error?.response?.data?.error || '';
    metrics.value = null;
  } finally {
    loading.value = false;
  }
};

const setToday = () => {
  selectedDate.value = todayIso();
};

// refetch on date change (with URL sync) — but ignore invalid input
watch(selectedDate, value => {
  if (!ISO_RE.test(value || '')) return;
  syncUrl();
  fetchData();
});

watch(accountId, () => {
  metrics.value = null;
  fetchData();
});

onMounted(() => {
  syncUrl();
  fetchData();
});

const isToday = computed(() => effectiveDate.value === todayIso());
const formattedDate = computed(() => {
  const parts = (effectiveDate.value || '').split('-');
  return parts.length === 3 ? `${parts[2]}/${parts[1]}/${parts[0]}` : effectiveDate.value;
});
const lastUpdated = computed(() =>
  generatedAt.value ? new Date(generatedAt.value).toLocaleString('pt-BR') : DASH
);

// ---- copiable text report --------------------------------------------------
const reportText = computed(() => {
  const x = m.value;
  const when = isToday.value ? 'Hoje' : formattedDate.value;
  return [
    `📊 Relatório da tua conta – ${when}`,
    ``,
    `💳 Conta: ${x.conta_anuncio || DASH}`,
    `💰 Fatura META: ${brl(x.saldo_meta)}`,
    ``,
    `📈 Gasto Ads: ${brl(x.gasto_ads)}`,
    `📉 Gasto Fornecedor: ${brl(x.gasto_fornecedor)}`,
    `❤️ L do amor (12,5%): ${brl(x.l_do_amor)}`,
    `🏦 Taxa ExPay: ${brl(x.taxa_checkout)}`,
    ``,
    `💰 Faturamento: ${brl(x.faturamento)}`,
    `🏦 Faturado Real: ${brl(x.faturado_real)}`,
    ``,
    `⚡ Vendas no primeiro dia: ${brl(x.receita_1d_total)} (${pct(x.percentual_1d)})`,
    `🚀 ROI (1º dia): ${dec(x.roi_1d)}`,
    ``,
    `📊 Leads`,
    `• Rastreado: ${int(x.leads_rastreada)}`,
    `• Não rastreado: ${int(x.leads_nao_rastreada)}`,
    `• Total: ${int(x.leads_total)}`,
    ``,
    `⚡ 1º Dia (Receita)`,
    `• Rastreado: ${brl(x.receita_1d_rastreada)} (${pct(x.percentual_receita_rastreada)})`,
    `• Não rastreado: ${brl(x.receita_1d_nao_rastreada)} (${pct(x.percentual_receita_nao_rastreada)})`,
    ``,
    `💡 Compradores (1º dia)`,
    `• Rastreado: ${int(x.compradores_1d_rastreado)}`,
    `• Não rastreado: ${int(x.compradores_1d_nao_rastreado)}`,
    `• Total: ${int(x.compradores_1d_total)}`,
    ``,
    `💸 CPL (Ads): ${brl(x.cpl_ads)}`,
    ``,
    `🧾 Total de vendas: ${int(x.total_vendas)}`,
    `📊 Conversão: ${pct(x.conversao)}`,
    ``,
    `✅ Resultado: ${brl(x.resultado)} (${pct(x.resultado_percentual)})`,
  ].join('\n');
});

const copyReport = async () => {
  try {
    await navigator.clipboard.writeText(reportText.value);
    useAlert(t('ENGAGEHUB.REPORT_COPIED'));
  } catch {
    useAlert(t('ENGAGEHUB.ERROR_GENERIC'));
  }
};
</script>

<template>
  <div class="flex flex-col w-full h-full overflow-auto bg-n-background">
    <!-- header -->
    <div class="flex flex-wrap items-start justify-between gap-3 px-6 py-5 border-b border-n-weak">
      <div>
        <h1 class="text-xl font-semibold text-n-slate-12">{{ $t('ENGAGEHUB.TITLE') }}</h1>
        <p class="text-sm text-n-slate-11">{{ $t('ENGAGEHUB.SUBTITLE') }}</p>
        <p class="mt-1 text-xs text-n-slate-10">
          {{ currentAccount.name }} · {{ currentUser.name }} ·
          {{ $t('ENGAGEHUB.UPDATED_AT') }}: {{ lastUpdated }}
        </p>
        <p class="text-xs text-n-slate-10">
          {{ $t('ENGAGEHUB.DATA_REF') }}: {{ formattedDate }}
        </p>
      </div>
      <div class="flex items-end gap-2">
        <label class="flex flex-col gap-1">
          <span class="text-xs text-n-slate-11">{{ $t('ENGAGEHUB.DATE_LABEL') }}</span>
          <input
            v-model="selectedDate"
            type="date"
            class="!mb-0 text-sm"
            :disabled="loading"
          />
        </label>
        <NextButton
          :label="$t('ENGAGEHUB.TODAY')"
          slate
          faded
          :disabled="loading || isToday"
          @click="setToday"
        />
        <NextButton
          icon="i-lucide-refresh-cw"
          :label="$t('ENGAGEHUB.REFRESH')"
          :is-loading="loading"
          :disabled="loading"
          @click="fetchData"
        />
      </div>
    </div>

    <!-- loading -->
    <div v-if="loading && !metrics" class="flex items-center justify-center flex-1 text-n-slate-11 py-20">
      {{ $t('ENGAGEHUB.LOADING') }}
    </div>

    <!-- error -->
    <div v-else-if="errored" class="flex flex-col items-center justify-center flex-1 gap-3 py-20 text-center">
      <span class="i-lucide-triangle-alert text-4xl text-n-ruby-9" />
      <p class="text-sm text-n-slate-11">{{ errorMessage || $t('ENGAGEHUB.ERROR_DATE') }}</p>
      <NextButton :label="$t('ENGAGEHUB.RETRY')" @click="fetchData" />
    </div>

    <!-- empty -->
    <div v-else-if="empty" class="flex flex-col items-center justify-center flex-1 gap-3 py-20 text-center">
      <span class="i-lucide-inbox text-4xl text-n-slate-9" />
      <p class="text-sm text-n-slate-11">{{ $t('ENGAGEHUB.EMPTY_DATE', { date: formattedDate }) }}</p>
    </div>

    <!-- content -->
    <div v-else-if="metrics" class="flex flex-col gap-6 p-6">
      <!-- top cards -->
      <div class="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3">
        <div class="rounded-xl border border-n-weak bg-n-solid-1 p-4">
          <p class="text-xs text-n-slate-11">{{ $t('ENGAGEHUB.CARDS.REVENUE') }}</p>
          <p class="mt-1 text-lg font-semibold text-n-slate-12">{{ brl(m.faturamento) }}</p>
        </div>
        <div class="rounded-xl border border-n-weak bg-n-solid-1 p-4">
          <p class="text-xs text-n-slate-11">{{ $t('ENGAGEHUB.CARDS.REAL_REVENUE') }}</p>
          <p class="mt-1 text-lg font-semibold text-n-slate-12">{{ brl(m.faturado_real) }}</p>
        </div>
        <div class="rounded-xl border border-n-weak bg-n-solid-1 p-4">
          <p class="text-xs text-n-slate-11">{{ $t('ENGAGEHUB.CARDS.RESULT') }}</p>
          <p class="mt-1 text-lg font-semibold" :class="toneClass(tone(m.resultado))">{{ brl(m.resultado) }}</p>
          <p class="text-xs" :class="toneClass(tone(m.resultado))">{{ pct(m.resultado_percentual) }}</p>
        </div>
        <div class="rounded-xl border border-n-weak bg-n-solid-1 p-4">
          <p class="text-xs text-n-slate-11">{{ $t('ENGAGEHUB.CARDS.ROI') }}</p>
          <p class="mt-1 text-lg font-semibold" :class="toneClass(roiTone(m.roi_1d))">{{ dec(m.roi_1d) }}</p>
        </div>
        <div class="rounded-xl border border-n-weak bg-n-solid-1 p-4">
          <p class="text-xs text-n-slate-11">{{ $t('ENGAGEHUB.CARDS.ADS_SPEND') }}</p>
          <p class="mt-1 text-lg font-semibold text-n-slate-12">{{ brl(m.gasto_ads) }}</p>
        </div>
        <div class="rounded-xl border border-n-weak bg-n-solid-1 p-4">
          <p class="text-xs text-n-slate-11">{{ $t('ENGAGEHUB.CARDS.SUPPLIER_SPEND') }}</p>
          <p class="mt-1 text-lg font-semibold text-n-slate-12">{{ brl(m.gasto_fornecedor) }}</p>
        </div>
      </div>

      <!-- sections grid -->
      <div class="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <!-- ad account -->
        <div class="rounded-xl border border-n-weak bg-n-solid-1 p-4">
          <h3 class="mb-3 text-sm font-semibold text-n-slate-12">{{ $t('ENGAGEHUB.SECTIONS.AD_ACCOUNT') }}</h3>
          <div class="flex flex-col gap-1.5 text-sm">
            <div class="flex justify-between"><span class="text-n-slate-11">{{ $t('ENGAGEHUB.LABELS.META_ACCOUNT') }}</span><span class="text-n-slate-12">{{ m.conta_anuncio || DASH }}</span></div>
            <div class="flex justify-between"><span class="text-n-slate-11">{{ $t('ENGAGEHUB.LABELS.META_BALANCE') }}</span><span class="text-n-slate-12">{{ brl(m.saldo_meta) }}</span></div>
            <div class="flex justify-between"><span class="text-n-slate-11">{{ $t('ENGAGEHUB.CARDS.ADS_SPEND') }}</span><span class="text-n-slate-12">{{ brl(m.gasto_ads) }}</span></div>
          </div>
        </div>

        <!-- costs -->
        <div class="rounded-xl border border-n-weak bg-n-solid-1 p-4">
          <h3 class="mb-3 text-sm font-semibold text-n-slate-12">{{ $t('ENGAGEHUB.SECTIONS.COSTS') }}</h3>
          <div class="flex flex-col gap-1.5 text-sm">
            <div class="flex justify-between"><span class="text-n-slate-11">{{ $t('ENGAGEHUB.CARDS.ADS_SPEND') }}</span><span class="text-n-slate-12">{{ brl(m.gasto_ads) }}</span></div>
            <div class="flex justify-between"><span class="text-n-slate-11">{{ $t('ENGAGEHUB.CARDS.SUPPLIER_SPEND') }}</span><span class="text-n-slate-12">{{ brl(m.gasto_fornecedor) }}</span></div>
            <div class="flex justify-between"><span class="text-n-slate-11">{{ $t('ENGAGEHUB.LABELS.L_DO_AMOR') }}</span><span class="text-n-slate-12">{{ brl(m.l_do_amor) }}</span></div>
            <div class="flex justify-between"><span class="text-n-slate-11">{{ $t('ENGAGEHUB.LABELS.EXPAY') }}</span><span class="text-n-slate-12">{{ brl(m.taxa_checkout) }}</span></div>
          </div>
        </div>

        <!-- leads -->
        <div class="rounded-xl border border-n-weak bg-n-solid-1 p-4">
          <h3 class="mb-3 text-sm font-semibold text-n-slate-12">{{ $t('ENGAGEHUB.SECTIONS.LEADS') }}</h3>
          <div class="flex flex-col gap-1.5 text-sm">
            <div class="flex justify-between"><span class="text-n-slate-11">{{ $t('ENGAGEHUB.LABELS.TRACKED') }}</span><span class="text-n-slate-12">{{ int(m.leads_rastreada) }}</span></div>
            <div class="flex justify-between"><span class="text-n-slate-11">{{ $t('ENGAGEHUB.LABELS.UNTRACKED') }}</span><span class="text-n-slate-12">{{ int(m.leads_nao_rastreada) }}</span></div>
            <div class="flex justify-between"><span class="text-n-slate-11">{{ $t('ENGAGEHUB.LABELS.TOTAL') }}</span><span class="text-n-slate-12">{{ int(m.leads_total) }}</span></div>
            <div class="flex justify-between"><span class="text-n-slate-11">{{ $t('ENGAGEHUB.LABELS.CPL') }}</span><span class="text-n-slate-12">{{ brl(m.cpl_ads) }}</span></div>
          </div>
        </div>

        <!-- first day revenue -->
        <div class="rounded-xl border border-n-weak bg-n-solid-1 p-4">
          <h3 class="mb-3 text-sm font-semibold text-n-slate-12">{{ $t('ENGAGEHUB.SECTIONS.FIRST_DAY_REVENUE') }}</h3>
          <div class="flex flex-col gap-1.5 text-sm">
            <div class="flex justify-between"><span class="text-n-slate-11">{{ $t('ENGAGEHUB.LABELS.TRACKED') }}</span><span class="text-n-slate-12">{{ brl(m.receita_1d_rastreada) }} ({{ pct(m.percentual_receita_rastreada) }})</span></div>
            <div class="flex justify-between"><span class="text-n-slate-11">{{ $t('ENGAGEHUB.LABELS.UNTRACKED') }}</span><span class="text-n-slate-12">{{ brl(m.receita_1d_nao_rastreada) }} ({{ pct(m.percentual_receita_nao_rastreada) }})</span></div>
            <div class="flex justify-between"><span class="text-n-slate-11">{{ $t('ENGAGEHUB.LABELS.TOTAL') }}</span><span class="text-n-slate-12">{{ brl(m.receita_1d_total) }} ({{ pct(m.percentual_1d) }})</span></div>
          </div>
        </div>

        <!-- first day buyers -->
        <div class="rounded-xl border border-n-weak bg-n-solid-1 p-4">
          <h3 class="mb-3 text-sm font-semibold text-n-slate-12">{{ $t('ENGAGEHUB.SECTIONS.FIRST_DAY_BUYERS') }}</h3>
          <div class="flex flex-col gap-1.5 text-sm">
            <div class="flex justify-between"><span class="text-n-slate-11">{{ $t('ENGAGEHUB.LABELS.TRACKED') }}</span><span class="text-n-slate-12">{{ int(m.compradores_1d_rastreado) }}</span></div>
            <div class="flex justify-between"><span class="text-n-slate-11">{{ $t('ENGAGEHUB.LABELS.UNTRACKED') }}</span><span class="text-n-slate-12">{{ int(m.compradores_1d_nao_rastreado) }}</span></div>
            <div class="flex justify-between"><span class="text-n-slate-11">{{ $t('ENGAGEHUB.LABELS.TOTAL') }}</span><span class="text-n-slate-12">{{ int(m.compradores_1d_total) }}</span></div>
          </div>
        </div>

        <!-- sales -->
        <div class="rounded-xl border border-n-weak bg-n-solid-1 p-4">
          <h3 class="mb-3 text-sm font-semibold text-n-slate-12">{{ $t('ENGAGEHUB.SECTIONS.SALES') }}</h3>
          <div class="flex flex-col gap-1.5 text-sm">
            <div class="flex justify-between"><span class="text-n-slate-11">{{ $t('ENGAGEHUB.LABELS.TOTAL_SALES') }}</span><span class="text-n-slate-12">{{ int(m.total_vendas) }}</span></div>
            <div class="flex justify-between"><span class="text-n-slate-11">{{ $t('ENGAGEHUB.LABELS.TOTAL_ORDERS') }}</span><span class="text-n-slate-12">{{ int(m.total_pedidos) }}</span></div>
            <div class="flex justify-between"><span class="text-n-slate-11">{{ $t('ENGAGEHUB.LABELS.CONVERSION') }}</span><span class="text-n-slate-12">{{ pct(m.conversao) }}</span></div>
          </div>
        </div>
      </div>

      <!-- text report -->
      <div class="rounded-xl border border-n-weak bg-n-solid-1 p-4">
        <div class="flex items-center justify-between mb-3">
          <h3 class="text-sm font-semibold text-n-slate-12">{{ $t('ENGAGEHUB.SECTIONS.REPORT') }}</h3>
          <NextButton icon="i-lucide-clipboard" :label="$t('ENGAGEHUB.COPY_REPORT')" slate faded sm @click="copyReport" />
        </div>
        <pre class="whitespace-pre-wrap text-xs text-n-slate-11 font-sans">{{ reportText }}</pre>
      </div>
    </div>
  </div>
</template>
